import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Chip, Popover, TextField, IconButton, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { tagActions } from '../../store';
import { tagColors, renderColor } from './colors';

const MAX_NAME_LENGTH = 15;

export const TagComponent = ({ taskId, taskTags = [], projectId, onTagsChange, showTags = true }) => {
  const dispatch = useDispatch();
  const allTags = useSelector((state) => state.tags.tags);

  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColorIdx, setNewTagColorIdx] = useState(0);
  const [editTag, setEditTag] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColorIdx, setEditColorIdx] = useState(0);
  const [mode, setMode] = useState('select'); // 'select' | 'create' | 'edit'
  const pendingTagName = useRef(null);
  const prevTagCount = useRef(allTags?.length || 0);

  // Fetch tags for the project when popover opens
  useEffect(() => {
    if (anchorEl && projectId) {
      dispatch(tagActions.getTags({ project: projectId }));
    }
  }, [anchorEl, projectId, dispatch]);

  // Sync selectedId from taskTags when popover is closed
  const currentTagId = taskTags?.[0]?._id || taskTags?.[0] || null;
  useEffect(() => {
    if (!anchorEl) setSelectedId(currentTagId);
  }, [currentTagId, anchorEl]);

  // Auto-select newly created tags
  useEffect(() => {
    if (pendingTagName.current && allTags?.length > prevTagCount.current) {
      const newTag = allTags.find(
        (t) => t.name === pendingTagName.current
      );
      if (newTag) {
        setSelectedId(newTag._id);
        pendingTagName.current = null;
      }
    }
    prevTagCount.current = allTags?.length || 0;
  }, [allTags]);

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setSelectedId(currentTagId);
    setAnchorEl(e.currentTarget);
    setMode('select');
    setSearch('');
  };

  const handleSaveAndClose = () => {
    if (onTagsChange) onTagsChange(selectedId ? [selectedId] : []);
    setAnchorEl(null);
    setMode('select');
    setSearch('');
    setNewTagName('');
    setEditTag(null);
  };

  const selectTag = (tagId) => {
    // Toggle: click same tag deselects, click different tag selects
    setSelectedId((prev) => prev === tagId ? null : tagId);
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;
    const colorPair = tagColors[newTagColorIdx];
    const trimmedName = newTagName.trim();
    pendingTagName.current = trimmedName;
    dispatch(tagActions.createTag({
      name: trimmedName,
      color: colorPair.color,
      project: projectId,
    }));
    setNewTagName('');
    setNewTagColorIdx((prev) => (prev + 1) % tagColors.length);
    setMode('select');
  };

  const handleEditTag = () => {
    if (!editTag || !editName.trim()) return;
    const colorPair = tagColors[editColorIdx];
    dispatch(tagActions.updateTag(editTag._id, {
      name: editName.trim(),
      color: colorPair.color,
    }));
    setEditTag(null);
    setMode('select');
  };

  const handleDeleteTag = (tagId) => {
    dispatch(tagActions.deleteTag(tagId));
    if (selectedId === tagId) setSelectedId(null);
    setEditTag(null);
    setMode('select');
  };

  const startEdit = (tag) => {
    setEditTag(tag);
    setEditName(tag.name);
    const idx = tagColors.findIndex((tc) => tc.color === tag.color);
    setEditColorIdx(idx >= 0 ? idx : 0);
    setMode('edit');
  };

  const filtered = (allTags || []).filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    if (a._id === selectedId) return -1;
    if (b._id === selectedId) return 1;
    return a.name.localeCompare(b.name);
  });

  const colors = renderColor;

  // Current tag display
  const currentTag = taskTags?.[0];
  const currentColor = currentTag ? colors(currentTag.color) : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {showTags && currentTag ? (
        <Chip
          label={currentTag.name}
          size="small"
          onClick={handleOpen}
          sx={{
            height: 22, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            backgroundColor: currentColor?.background, color: currentColor?.color,
            '&:hover': { opacity: 0.85 },
          }}
        />
      ) : showTags ? (
        <Typography
          variant="caption"
          onClick={handleOpen}
          sx={{
            color: 'text.disabled', fontSize: 12, cursor: 'pointer',
            px: 0.5, py: 0.25, borderRadius: 0.5,
            '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' },
          }}
        >
          + Tag
        </Typography>
      ) : (
        <IconButton size="small" onClick={handleOpen} sx={{ width: 20, height: 20 }}>
          <AddIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        </IconButton>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleSaveAndClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 260, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
      >
        {mode === 'select' && (
          <Box sx={{ p: 1.5 }}>
            <TextField
              fullWidth size="small" placeholder="Search tags..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 12 },
              }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {sorted.map((tag) => {
                const isSelected = tag._id === selectedId;
                const c = colors(tag.color);
                return (
                  <Box
                    key={tag._id}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 0.5, px: 0.5, borderRadius: 1, cursor: 'pointer',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}
                      onClick={() => selectTag(tag._id)}
                    >
                      <Box sx={{
                        width: 16, height: 16, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'text.disabled',
                        p: '2px',
                      }}>
                        {isSelected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                      <Chip
                        label={tag.name} size="small"
                        sx={{ height: 22, fontSize: 11, fontWeight: 600, backgroundColor: c.background, color: c.color }}
                      />
                    </Box>
                    <IconButton size="small" onClick={() => startEdit(tag)} sx={{ width: 22, height: 22 }}>
                      <EditIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    </IconButton>
                  </Box>
                );
              })}
              {sorted.length === 0 && search && (
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', py: 1 }}>
                  No tags found
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Chip
                icon={<AddIcon sx={{ fontSize: 14 }} />}
                label="Create tag"
                size="small"
                onClick={() => { setMode('create'); setNewTagName(search); }}
                sx={{ fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              />
              <Chip
                label="Done"
                size="small"
                onClick={handleSaveAndClose}
                sx={{ fontSize: 11, fontWeight: 700, backgroundColor: 'primary.main', color: '#fff', cursor: 'pointer' }}
              />
            </Box>
          </Box>
        )}

        {mode === 'create' && (
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>Create Tag</Typography>
              <IconButton size="small" onClick={() => setMode('select')}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <TextField
              fullWidth size="small" placeholder="Tag name"
              value={newTagName}
              onChange={(e) => e.target.value.length <= MAX_NAME_LENGTH && setNewTagName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
              InputProps={{ sx: { fontSize: 12 } }}
              helperText={`${newTagName.length}/${MAX_NAME_LENGTH}`}
              FormHelperTextProps={{ sx: { textAlign: 'right', mt: 0.25, fontSize: 10 } }}
              sx={{ mb: 1 }}
            />

            {newTagName.trim() && (
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={newTagName}
                  size="small"
                  sx={{
                    height: 24, fontSize: 11, fontWeight: 600,
                    backgroundColor: tagColors[newTagColorIdx].background,
                    color: tagColors[newTagColorIdx].color,
                  }}
                />
              </Box>
            )}

            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Color</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {tagColors.map((tc, idx) => (
                <Box
                  key={idx}
                  onClick={() => setNewTagColorIdx(idx)}
                  sx={{
                    width: 24, height: 24, borderRadius: '50%', backgroundColor: tc.color,
                    cursor: 'pointer',
                    border: newTagColorIdx === idx ? '3px solid' : '3px solid transparent',
                    borderColor: newTagColorIdx === idx ? 'text.primary' : 'transparent',
                    transition: 'border-color 0.15s',
                  }}
                />
              ))}
            </Box>

            <Chip
              label="Create"
              size="small"
              onClick={handleCreateTag}
              disabled={!newTagName.trim()}
              sx={{ fontSize: 11, fontWeight: 700, backgroundColor: 'primary.main', color: '#fff', cursor: 'pointer', width: '100%' }}
            />
          </Box>
        )}

        {mode === 'edit' && editTag && (
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>Edit Tag</Typography>
              <Box>
                <IconButton size="small" onClick={() => handleDeleteTag(editTag._id)} sx={{ color: 'error.main' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton size="small" onClick={() => setMode('select')}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            <TextField
              fullWidth size="small" placeholder="Tag name"
              value={editName}
              onChange={(e) => e.target.value.length <= MAX_NAME_LENGTH && setEditName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEditTag(); }}
              InputProps={{ sx: { fontSize: 12 } }}
              helperText={`${editName.length}/${MAX_NAME_LENGTH}`}
              FormHelperTextProps={{ sx: { textAlign: 'right', mt: 0.25, fontSize: 10 } }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ mb: 1 }}>
              <Chip
                label={editName || editTag.name}
                size="small"
                sx={{
                  height: 24, fontSize: 11, fontWeight: 600,
                  backgroundColor: tagColors[editColorIdx].background,
                  color: tagColors[editColorIdx].color,
                }}
              />
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Color</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {tagColors.map((tc, idx) => (
                <Box
                  key={idx}
                  onClick={() => setEditColorIdx(idx)}
                  sx={{
                    width: 24, height: 24, borderRadius: '50%', backgroundColor: tc.color,
                    cursor: 'pointer',
                    border: editColorIdx === idx ? '3px solid' : '3px solid transparent',
                    borderColor: editColorIdx === idx ? 'text.primary' : 'transparent',
                    transition: 'border-color 0.15s',
                  }}
                />
              ))}
            </Box>

            <Chip
              label="Save"
              size="small"
              onClick={handleEditTag}
              disabled={!editName.trim()}
              sx={{ fontSize: 11, fontWeight: 700, backgroundColor: 'primary.main', color: '#fff', cursor: 'pointer', width: '100%' }}
            />
          </Box>
        )}
      </Popover>
    </Box>
  );
};
